import React, { useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useGetUsersQuery } from "../../store/apis/usersApi";
import { assignableUsers, personLabel } from "../../utils/companyAssignments";

interface Props {
  /** id de la persona elegida, o "" para no filtrar */
  value: string;
  onChange: (userId: string) => void;
  /** La persona que está mirando, que sale la primera como "Yo" */
  currentUserId?: string;
}

type Option = { id: string; label: string };

/**
 * Filtra por persona asignada a la empresa, en el listado de briefings y en el WIP. Lo
 * que sale depende de su papel en cada empresa y lo decide el backend: como partner
 * success, todo; como responsable de una plataforma, los briefings con alguna línea en
 * ella (en el WIP, solo esas líneas).
 */
const AssignedUserFilter: React.FC<Props> = ({
  value,
  onChange,
  currentUserId,
}) => {
  const { t } = useTranslation();
  const { data: usersData, isFetching } = useGetUsersQuery();

  const options = useMemo<Option[]>(() => {
    const users = assignableUsers(usersData?.data ?? []).map((user) => ({
      id: user.id,
      label: personLabel(user),
    }));
    const me = users.find((user) => user.id === currentUserId);
    if (!me) return users;
    return [
      {
        id: me.id,
        label: t("briefings.assignedFilter.me", "Yo ({{name}})", {
          name: me.label,
        }),
      },
      ...users.filter((user) => user.id !== me.id),
    ];
  }, [usersData, currentUserId, t]);

  const selected = options.find((option) => option.id === value) ?? null;

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 240 }}
      options={options}
      value={selected}
      loading={isFetching}
      onChange={(_, option) => onChange(option?.id ?? "")}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      getOptionLabel={(option) => option.label}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("briefings.assignedFilter.label", "Persona asignada")}
          placeholder={t("briefings.assignedFilter.anyone", "Cualquiera")}
        />
      )}
    />
  );
};

export default AssignedUserFilter;
